import { Illustration, IllustrationName } from '@/components/icons/Illustration'
import { Button } from '@/components/ui/button'
import { Card, CardAction, CardContent, CardFooter, CardHeader, CardTitle } from '@/components/ui/card'
import { IndeterminateLoadingBar } from '@/components/ui/IndeterminateLoadingBar'
import { cn } from '@/lib/utils'

/**
 * Presentational chrome for the Confirm information overview. Kept free of data hooks so the
 * design-system prototype renders the same components the application does — a change here
 * shows up in both rather than needing to be made twice.
 */

export function InfoRow({ label, value }: { label: string; value: string }) {
  return (
    // A label column from md, so a card of rows reads as one table; on phones each label sits on its value.
    <div className="flex flex-col gap-0.5 md:flex-row md:gap-4">
      <span className="text-ink-400 md:w-44 md:shrink-0">{label}</span>
      <span className="text-foreground min-w-0 wrap-anywhere">{value}</span>
    </div>
  )
}

export function SectionCard({
  title,
  children,
  action,
  error,
  warning,
  loading,
  footer,
  onClick,
}: {
  title: string
  children: React.ReactNode
  action?: React.ReactNode
  error?: boolean
  /** Something is still owed on this card, but nothing is blocking — amber, not destructive. */
  warning?: boolean
  /** Work is still running on this card's data — shows a bar across the top edge. */
  loading?: boolean
  /** Aside under the card's content — secondary guidance, not part of the summary. */
  footer?: React.ReactNode
  /** Makes the whole card a pointer target; keep a button in `action` for the keyboard. */
  onClick?: () => void
}) {
  return (
    <Card
      onClick={onClick}
      className={cn(
        'gap-2 shadow-none',
        onClick && 'cursor-pointer',
        warning && 'border-warning-d1',
        // Blocking wins the border: the customer can leave a warning alone.
        error && 'border-destructive'
      )}
    >
      {/* -mt-6 reaches the top edge; mb-3 keeps the header at the same 24px, bar or not. */}
      {loading && <IndeterminateLoadingBar className="-mt-6 mb-3 rounded-t-md" />}
      <CardHeader>
        <CardTitle className="text-ink flex items-center gap-2 text-base font-semibold">{title}</CardTitle>
        {action && <CardAction>{action}</CardAction>}
      </CardHeader>
      <CardContent className="flex flex-col gap-3">{children}</CardContent>
      {footer && <CardFooter className="text-muted-foreground pt-4 text-xs">{footer}</CardFooter>}
    </Card>
  )
}

/** An illustration, a label, and a trailing action — the shape every summary row shares. */
export function IllustratedRow({
  illustration,
  label,
  action,
  labelClassName,
  muted,
}: {
  illustration: IllustrationName
  label: React.ReactNode
  action?: React.ReactNode
  labelClassName?: string
  /** Nothing on file yet — lightens the artwork so the row reads as an empty state. */
  muted?: boolean
}) {
  return (
    <div className="flex w-full items-center gap-3">
      <Illustration name={illustration} muted={muted} />
      <div className={labelClassName}>{label}</div>
      {action && <div className="ml-auto">{action}</div>}
    </div>
  )
}

export function EditButton({ onClick, label }: { onClick?: () => void; label?: string }) {
  return (
    <Button variant="outline" onClick={onClick} aria-label={label}>
      Edit
    </Button>
  )
}
